/**
 * Step1_AziendaData — Dati azienda (tutti i campi del modello Azienda).
 * Layout a griglia 2 colonne con freccia di scroll in basso.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Building2, ChevronDown } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function StepAziendaData({ data, onChange, errors }) {
  const [settori, setSettori] = useState([]);
  const [showArrow, setShowArrow] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/store/settori/`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data.results || [];
        setSettori(list);
      })
      .catch(() => setSettori([]));
  }, []);

  // Check if the form overflows and needs scrolling
  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setShowArrow(el.scrollHeight > el.clientHeight + 10 &&
      el.scrollTop + el.clientHeight < el.scrollHeight - 10);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    checkOverflow();
    el.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow]);

  const scrollDown = () => {
    const el = containerRef.current;
    if (el) el.scrollBy({ top: 200, behavior: 'smooth' });
  };

  const handleChange = (field) => (e) => {
    onChange({ ...data, [field]: e.target.value });
  };

  const handleUrlBlur = () => {
    const v = (data.url || '').trim();
    if (v && !/^https?:\/\//i.test(v)) {
      onChange({ ...data, url: `https://${v}` });
    }
  };

  const fieldError = (field) => {
    const key = `azienda.${field}`;
    const err = errors?.[key] || errors?.[field];
    if (!err) return null;
    const msg = Array.isArray(err) ? err[0] : err;
    return <p className="text-red-500 text-[11px] mt-0.5">{msg}</p>;
  };

  const ic =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition-shadow';

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-5 h-5 text-yellow-600" />
        <h2 className="text-lg font-bold text-gray-900">Dati Azienda</h2>
      </div>

      {/* Scrollable form area — fills remaining card height */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-y-auto pr-2"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
          {/* Nome azienda */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Azienda <span className="text-red-500">*</span>
            </label>
            <input type="text" value={data.nome_azienda || ''} onChange={handleChange('nome_azienda')} className={ic} />
            {fieldError('nome_azienda')}
          </div>

          {/* Partita IVA */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Partita IVA <span className="text-red-500">*</span>
            </label>
            <input type="text" value={data.partita_iva || ''} onChange={handleChange('partita_iva')} maxLength={11} className={ic} />
            {fieldError('partita_iva')}
          </div>

          {/* Codice Fiscale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
            <input type="text" value={data.codice_fiscale || ''} onChange={handleChange('codice_fiscale')} className={ic} />
            {fieldError('codice_fiscale')}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input type="email" value={data.email || ''} onChange={handleChange('email')} className={ic} />
            {fieldError('email')}
          </div>

          {/* PEC */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PEC</label>
            <input type="email" value={data.pec || ''} onChange={handleChange('pec')} className={ic} />
          </div>

          {/* URL aziendale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL aziendale</label>
            <input type="url" value={data.url || ''} onChange={handleChange('url')} onBlur={handleUrlBlur} placeholder="www.azienda.it" className={ic} />
          </div>

          {/* Telefono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
            <input type="tel" value={data.telefono || ''} onChange={handleChange('telefono')} className={ic} />
          </div>

          {/* Indirizzo */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
            <input type="text" value={data.indirizzo || ''} onChange={handleChange('indirizzo')} className={ic} />
          </div>

          {/* CAP */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
            <input type="text" value={data.cap || ''} onChange={handleChange('cap')} maxLength={5} className={ic} />
          </div>

          {/* Città */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
            <input type="text" value={data.citta || ''} onChange={handleChange('citta')} className={ic} />
          </div>

          {/* Comune */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comune</label>
            <input type="text" value={data.comune || ''} onChange={handleChange('comune')} className={ic} />
          </div>

          {/* Provincia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
            <input type="text" value={data.provincia || ''} onChange={handleChange('provincia')} maxLength={2} className={ic} />
          </div>

          {/* Stato */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
            <input type="text" value={data.stato || ''} onChange={handleChange('stato')} className={ic} />
          </div>

          {/* N. Dipendenti */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">N. Dipendenti</label>
            <input type="number" value={data.numero_dipendenti || ''} onChange={handleChange('numero_dipendenti')} min={1} className={ic} />
          </div>

          {/* Fatturato Annuo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fatturato Annuo (€)</label>
            <input type="number" value={data.fatturato_annuo || ''} onChange={handleChange('fatturato_annuo')} min={0} step="0.01" className={ic} />
          </div>

          {/* Settore */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Settore</label>
            <select value={data.id_settore || ''} onChange={handleChange('id_settore')} className={ic}>
              <option value="">— Seleziona —</option>
              {settori.map((s) => (
                <option key={s.id_settore} value={s.id_settore}>
                  {s.settore || s.categoria || s.descrizione}
                </option>
              ))}
            </select>
          </div>

          {/* Codici ATECO */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Codici ATECO</label>
            <input type="text" value={data.codice_ateco || ''} onChange={handleChange('codice_ateco')} className={ic} />
          </div>
        </div>
      </div>

      {/* Scroll-down arrow indicator */}
      {showArrow && (
        <div className="flex justify-center mt-2">
          <button
            type="button"
            onClick={scrollDown}
            title="Scorri in basso per compilare gli altri campi"
            className="group flex flex-col items-center animate-bounce text-yellow-600 hover:text-yellow-700 transition-colors focus:outline-none"
          >
            <ChevronDown className="w-7 h-7" />
            <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-500 mt-0.5">
              Scorri per gli altri campi
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
